import { useState } from 'react'

import {
  Alert,
  Box,
  CollapsableCard,
  Form,
  Icon,
  Link,
  Popover,
  Text,
  ToggleButton,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import CategorySelector from 'modules/attendance/components/CustomTimeRanges/Content/Table/Modals/SpecialHourWizard/BasicDetails/CategorySelector'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import {
  ExtraHoursRulesData,
  ExtraHoursRulesDataField,
  validateMandatoryFields,
} from 'modules/attendance/lib/forms/utils/ExtraHourRulesWizardData'
import { EMPLOYEE_TYPE } from 'modules/attendance/lib/types'
import TimeCategoriesUtils from 'modules/attendance/lib/utils/TimeCategoriesUtils'

import HourRangeType from './HourRangeType'
import { isRangeOverlapping } from './utils/isRangeOverlapping'

const getMandatoryFields = (data: ExtraHoursRulesData): ExtraHoursRulesDataField[] => {
  const initialFields: ExtraHoursRulesDataField[] = [
    'timeSettingsCustomTimeRangeCategoryId',
    'fromHour',
  ]

  return data.isUnlimited() ? initialFields : initialFields.concat(['toHour'])
}

export const BasicDetailsStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<ExtraHoursRulesData>) => {
  const {
    extraHoursRanges,
    complementaryHoursRanges,
    timeSettingsCategories,
    hasContractHoursBaseline,
  } = useAttendanceSettingWorkCategories()
  const extraHoursCategories = TimeCategoriesUtils.extraHoursCategories(timeSettingsCategories)
  const nonDefaultExtraHoursCategories =
    TimeCategoriesUtils.nonDefaultCategories(extraHoursCategories)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { navigation } = useNavigation()

  const handleNext = async () => {
    setIsSubmitted(true)
    const mandatoryFields = getMandatoryFields(data)
    const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

    const fieldsErrors = {}

    if (data?.fromHour === 0) {
      const message = i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.validations.start_positive'
      )
      dataWithErrors.errors.fromMinute = message
      const existingField = invalidFields.findIndex((f) => Object.keys(f)[0] === 'fromHour')

      if (existingField && invalidFields[existingField]) {
        invalidFields[existingField].fromMinute = message
      } else {
        invalidFields.push({ fromMinute: message })
      }
    }

    if (data?.fromHour && !Number.isInteger(data.fromHour)) {
      const message = i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.validations.must_be_whole_number'
      )
      dataWithErrors.errors.fromHour = message
      invalidFields.push({ fromHour: message })
    }

    if (data?.toHour === 0) {
      const message = i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.validations.end_positive'
      )
      dataWithErrors.errors.toMinute = message
      const existingField = invalidFields.findIndex((f) => Object.keys(f)[0] === 'toHour')

      if (existingField && invalidFields[existingField]) {
        invalidFields[existingField].toMinute = message
      } else {
        invalidFields.push({ toMinute: message })
      }
    }

    if (data?.toHour && !Number.isInteger(data.toHour)) {
      const message = i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.validations.must_be_whole_number'
      )
      dataWithErrors.errors.toHour = message
      invalidFields.push({ toHour: message })
    }

    const rangeDiff = data?.toHour && data?.fromHour > data?.toHour
    const isComplementaryRule = data.employeeType === EMPLOYEE_TYPE.PART_TIME
    const relevantRanges = isComplementaryRule ? complementaryHoursRanges : extraHoursRanges
    const rangeOverlaps = rangeDiff ? false : isRangeOverlapping(relevantRanges, data)

    const overlapError = data.isUnlimited()
      ? i18n.t(
          'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.validations.overlaps_start_time'
        )
      : i18n.t(
          'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.validations.overlaps'
        )

    const updatedFieldsErrors = {
      ...fieldsErrors,
      ...(rangeOverlaps || rangeDiff
        ? {
            fromMinute: ' ',
            toMinute: ' ',
            rangeOverlaps: rangeOverlaps ? overlapError : '',
            rangeDiff: rangeDiff
              ? i18n.t(
                  'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.validations.start_end_hour'
                )
              : '',
          }
        : {}),
    }

    if (rangeOverlaps || rangeDiff) {
      invalidFields.push({ fromMinute: ' ' }, { toMinute: ' ' })
    }

    dataWithErrors.errors = {
      ...dataWithErrors.errors,
      ...updatedFieldsErrors,
    }

    onDataChanged(dataWithErrors)

    if (invalidFields.length) {
      return Promise.reject()
    }
  }
  useOnNext(handleNext)

  const handleDataChange = (newData: Partial<ExtraHoursRulesData>) => {
    const updatedData = { ...data, ...newData }

    if (isSubmitted) {
      const mandatoryFields = getMandatoryFields(updatedData)
      const { invalidFields, dataWithErrors } = validateMandatoryFields(
        mandatoryFields,
        updatedData
      )
      onDataChanged(dataWithErrors)

      if (!invalidFields.length) {
        setIsSubmitted(false)
      }
    } else {
      onDataChanged(updatedData)
    }
  }

  // Two-paragraph tooltip content as in design
  const applyBasedOnTooltipContent = (
    <Box gap="s8">
      <Box gap="s4" textAlign="left">
        <Text.Normal color="grey000White" weight="semibold" size="200">
          {i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.fixed_hours'
          )}
        </Text.Normal>
        <Text.Normal color="grey000White" size="200">
          {i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.apply_based_on_tooltip_fixed_description'
          )}
        </Text.Normal>
      </Box>
      <Box gap="s4" textAlign="left">
        <Text.Normal color="grey000White" weight="semibold" size="200">
          {i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.effective_hours'
          )}
        </Text.Normal>
        <Text.Normal color="grey000White" size="200">
          {i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.apply_based_on_tooltip_effective_description'
          )}
        </Text.Normal>
      </Box>
    </Box>
  )

  // Ensure tooltip opens on hover (controlled like rules index tooltips)
  const [applyTooltipOpen, toggleApplyTooltip] = useToggle(false)

  // Show baseline selector behind feature flag or if current value is fixed (contract) hours
  const showApplyBasedOnSelector =
    hasContractHoursBaseline ||
    data.baseline === 'contract_hours' ||
    (data.isEdit && data.originalBaseline === 'contract_hours')

  const openTooltip = toggleApplyTooltip(true)
  const closeTooltip = toggleApplyTooltip(false)

  return (
    <Box flexDirection="row" gap="s48">
      <Form padding="s0" notRounded>
        <Box gap="s24" width="s400">
          <Box gap="s8">
            <Text.Header2 weight="semibold">
              {i18n.t(
                'time_tracking.settings.work_time_categories.common.rules.new_rule_wizard.basic_details.title'
              )}
            </Text.Header2>
            <Text.Normal>
              {i18n.t(
                'time_tracking.settings.work_time_categories.common.rules.new_rule_wizard.basic_details.description'
              )}
            </Text.Normal>
          </Box>
          <CategorySelector
            label={i18n.t(
              'time_tracking.settings.work_time_categories.common.rules.new_rule_wizard.basic_details.rule_name'
            )}
            timeSettingsCategories={TimeCategoriesUtils.orderByName(nonDefaultExtraHoursCategories)}
            onChange={(value) => handleDataChange({ timeSettingsCustomTimeRangeCategoryId: value })}
            value={data.timeSettingsCustomTimeRangeCategoryId}
            error={isSubmitted ? data.errors?.timeSettingsCustomTimeRangeCategoryId : undefined}
            canCreateSettingCategory={data.canCreateSettingCategory}
            categoryType="extra_hour"
            placeholder={
              TimeCategoriesUtils.hasCategories(nonDefaultExtraHoursCategories)
                ? i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.select_or_create.not_empty'
                  )
                : i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.select_or_create.empty_extra_hours'
                  )
            }
          />
          {showApplyBasedOnSelector && (
            <Box gap="s8">
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Text.Normal weight="medium">
                  {i18n.t(
                    'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.apply_based_on'
                  )}
                </Text.Normal>
                <Popover.ItemList
                  attachment="bottom center"
                  targetAttachment="top center"
                  open={applyTooltipOpen}
                  toggle={toggleApplyTooltip}
                  gapDivider={false}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={i18n.t(
                      'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.apply_based_on'
                    )}
                    padding="s4"
                    cursor="pointer"
                    alignItems="center"
                    justifyContent="center"
                    onMouseEnter={openTooltip}
                    onMouseLeave={closeTooltip}
                    onFocus={openTooltip}
                    onBlur={closeTooltip}
                  >
                    <Icon.Medium icon="question-circle-line" color="grey800" />
                  </Box>
                  <Box
                    padding="s12"
                    gap="s8"
                    onMouseEnter={openTooltip}
                    onMouseLeave={closeTooltip}
                  >
                    {applyBasedOnTooltipContent}
                  </Box>
                </Popover.ItemList>
              </Box>
              <Box flexDirection="row" gap="s8" alignItems="center">
                <ToggleButton
                  toggled={data.baseline === 'contract_hours'}
                  onClick={() => handleDataChange({ baseline: 'contract_hours' })}
                  label={i18n.t(
                    'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.fixed_hours'
                  )}
                />
                <ToggleButton
                  toggled={data.baseline === 'effective_hours'}
                  onClick={() => handleDataChange({ baseline: 'effective_hours' })}
                  label={i18n.t(
                    'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.effective_hours'
                  )}
                />
              </Box>
            </Box>
          )}

          <Box gap="s8">
            <Text.Normal weight="medium">
              {i18n.t(
                'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.tracking_condition'
              )}
            </Text.Normal>
            <CollapsableCard
              open
              Card={
                <Box gap="s8">
                  <Text.Normal weight="semibold">
                    {i18n.t(
                      'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.title'
                    )}
                  </Text.Normal>
                  <Text.Normal>
                    {i18n.t(
                      'time_tracking.settings.work_time_categories.extra_hours_rules.new_rule_wizard.tracking_condition.description'
                    )}
                  </Text.Normal>
                </Box>
              }
              Content={
                <HourRangeType
                  data={data}
                  handleDataChange={handleDataChange}
                  isSubmitted={isSubmitted}
                />
              }
            />
          </Box>
        </Box>
      </Form>
      {data?.id && (
        <Box
          paddingTop="s120"
          onClick={() =>
            track(trackingEvents.timeTracking.goToWorkTimeCategoriesSettingsLinkPressed)
          }
        >
          <Alert color="info">
            <Text.Normal>
              {i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.extra_hours_edit_categories_hint'
              )}
              <Link.Href
                href={navigation.settings.time_categories_settings.fullPath()}
                target="_blank"
              >
                {i18n.t(
                  'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.edit_categories_hint_link'
                )}
              </Link.Href>
            </Text.Normal>
          </Alert>
        </Box>
      )}
    </Box>
  )
}

export const basicDetailsStep = (): WizardStep<ExtraHoursRulesData> => ({
  title: i18n.t(
    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.title'
  ),
  nextText: i18n.t('time_tracking.settings.time_blocks.categories.special_hours.wizard.next'),
  render: (props) => <BasicDetailsStep {...props} />,
  isCompleted: () => true,
})
